/**
 * URL → platform id parsers for the inline player.
 *
 * Each platform embeds a VOD, a clip and a live channel from different
 * identifiers, and all we are given is the watch URL. Split out of
 * `StreamPanel.tsx` — pure string work with nothing else in that component's
 * business, and the file was past the 400-line ceiling.
 */

/** Parse a Twitch VOD URL `https://www.twitch.tv/videos/<id>` → numeric id */
export function extractTwitchVideoId(url: string): string | undefined {
    const m = url.match(/twitch\.tv\/videos\/(\d+)/);
    return m?.[1];
}

/*
 * Parse a Twitch CLIP URL → slug. Clips and VODs are different embed surfaces
 * (https://clips.twitch.tv/embed?clip=<slug> vs the player.twitch.tv VOD URL),
 * so they need separate extraction. Covers the two URL forms Twitch returns:
 *   - https://clips.twitch.tv/<slug>
 *   - https://www.twitch.tv/<channel>/clip/<slug>
 *
 * Slug alphabet includes `.` for legacy Twitch clips issued before they
 * standardized on alphanumeric+hyphen — those dot-format slugs still resolve
 * on the embed endpoint, and a tighter regex would truncate them at the dot
 * and load a different (or non-existent) clip.
 */
export function extractTwitchClipSlug(url: string): string | undefined {
    const m = url.match(/clips\.twitch\.tv\/([A-Za-z0-9._-]+)/) ??
        url.match(/twitch\.tv\/[^/]+\/clip\/([A-Za-z0-9._-]+)/);
    return m?.[1];
}

/** Parse a YouTube watch/Shorts URL → 11-char video id; covers `?v=<id>` and `/shorts/<id>`. */
export function extractYouTubeVideoId(url: string): string | undefined {
    const m = url.match(/[?&]v=([A-Za-z0-9_-]{11})|\/shorts\/([A-Za-z0-9_-]{11})/);
    return m?.[1] ?? m?.[2];
}
